import {useEffect, useMemo, useReducer, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';
import {useHistory} from "react-router-dom";
import * as yup from 'yup';
import * as Sentry from "@sentry/react";
import {ErrorMessage, Formik} from 'formik';
import {NotificationManager} from "react-notifications";
import Input from '../../../common/simple/input';
import Button from '../../../common/Button';
import arrowInCircle from '../../../images/newDesign/arrow_in_white_circle.svg';
import VisaIcon from '../../../images/newDesign/cards/Visa.svg';
import MasterCardIcon from '../../../images/newDesign/cards/MasterCard.svg';
import DinersIcon from '../../../images/newDesign/cards/Diners.svg';
import chip from '../../../images/newDesign/cards/chip.svg';
import contactless from '../../../images/newDesign/cards/contactless.svg';
import AmericanExpressIcon from '../../../images/newDesign/cards/AmericanExpress.svg';
import UnknownIcon from '../../../images/cards/unknown-icon.svg';
import {hideLoader, showLoader} from "../../../store/common/slice";
import {GAEvent} from "../../../common/google_analytics/google_analytics";
import {constants} from "../../../common/constants/constants";
import ServerData from "../../../common/serverData";
import {triggerRewardsNotification} from "../../../managers/rewards/rewardsEvent";
import {resetTenant} from "../../../store/tenant/slice";
import PaymentFlowContainer from '../PaymentFlowContainer';
import {StyledCreditCardPage} from './CreditCardPage.styled';
import Money from "../../../common/utils/money";
import {useHotkeys} from "react-hotkeys-hook";


const POLLING_PERIOD = 2000;
const MAX_WAIT_TIME = 10000;
const CARDS = {
    ae: {image: AmericanExpressIcon},
    mc: {image: MasterCardIcon},
    diners: {image: DinersIcon},
    visa: {image: VisaIcon},
    unknown: {image: UnknownIcon}
};

export function CreditCardPage({ match }) {

    const { t } = useTranslation();
    const { buildingID, cartID } = match.params;
    const history = useHistory();
    const dispatch = useDispatch();

    const cardNumPlaceholder = useRef();
    const cvvPlaceholder = useRef();

    const ltr = useSelector(state => state.common.ltrEnabled);
    const paymentPlan = useSelector(state => state.tenant.paymentPlan);
    const tenantDetails = useSelector(state => state.tenant.details);
    const disabledInsurancePage = useSelector(state => state.tenant.disableInsurancePage ?? false);

    const [dryRun, setDryRun] = useState(false);
    useHotkeys('ctrl+shift+esc', toggleDryRun);

    const [tzlaHostedFieldsApiReady, setTzlaHostedFieldsApiReady] = useState(false);
    const [tzlaError, setTzlaError] = useState(null);
    const [tzlaFields, setTzlaFields] = useState(null);
    const [tzlaFieldsState, dispatchTzlaFieldsState] = useReducer(tzlaFieldsReducer, {
        credit_card_number: { valid: true, touched: false, empty: true },
        cvv: { valid: true, touched: false, empty: true },
    });

    const [orderStatusChecked, setOrderStatusChecked] = useState(false);
    const [date, setDate] = useState({
        year: '',
        month: ''
    });
    const [currentService, setCurrentService] = useState('unknown');
    const formSchema = useMemo(() => yup.object().shape(dryRun ? {} : {
        expireMonth: yup.string()
            .required(t('titles.forms.mandatory_field'))
            .min(1, t('titles.forms.min_length', {size: 1}))
            .max(2, t('titles.forms.max_length', {size: 2})),
        expireYear: yup.string()
            .required(t('titles.forms.mandatory_field'))
            .length(2),
    }), [dryRun]);

    // TODO: remove this hack after the redux storage will keep state between page reload
    useEffect(() => {
        if (paymentPlan == null) {
            console.log('Payment plan is null, presumably the local storage do not available on user agent/device');
            history.goBack();
        }
    }, []);

    useEffect(async () => {
        const order = await (new ServerData()).tenants.payments.getOrderStatus(cartID);
        const success = constants.PaymentPlanDetailsStatus.ALL_SUCCESS.includes(order?.payment_details?.state);
        if (success) {
            NotificationManager.warning(t('payments.cart_has_been_payed_already'), '', 5000);
            dispatch(resetTenant());
            if (disabledInsurancePage){
                history.push(`/status/${cartID}`);
            } else{
                history.push(`/${constants.pages.insuranceOffer}/${buildingID}/${tenantDetails?.apartmentNumber|| '1'}/${cartID}`);
            }
        } else {
            setOrderStatusChecked(true);
        }
    }, []);

    useEffect(() => {
        if (window.TzlaHostedFields == null) {
            const script = window.document.createElement('script');
            script.setAttribute('src', 'https://hf.tranzila.com/assets/js/thostedf.js');
            script.addEventListener('load', () => setTzlaHostedFieldsApiReady(true));
            script.addEventListener('error', handleTzlaHostedFieldsApiLoadError)
            window.document.head.appendChild(script);
        } else {
            setTzlaHostedFieldsApiReady(true);
        }
    }, []);

    useEffect(() => {
        if (paymentPlan != null && orderStatusChecked && tzlaHostedFieldsApiReady && tzlaFields == null && cardNumPlaceholder.current != null && cvvPlaceholder.current != null) {
            const credit_card_number_selector = `#${cardNumPlaceholder.current.id}`;
            const cvv_selector = `#${cvvPlaceholder.current.id}`;
            console.log('Initializing TzlaHostedFields');
            console.log('credit_card_number_selector', credit_card_number_selector, document.querySelector(credit_card_number_selector));
            console.log('cvv_selector', cvv_selector, document.querySelector(cvv_selector));
            const fields = window.TzlaHostedFields.create({
                sandbox: false,
                fields: {
                    credit_card_number: {
                        selector: credit_card_number_selector,
                        placeholder: '',
                        tabindex: 1,
                    },
                    cvv: {
                        selector: cvv_selector,
                        placeholder: '',
                        tabindex: 4
                    },
                },
                styles: {
                    'input': {
                        'direction': 'ltr',
                        // to avoid Auto Zoom in Input 'Text' tag - Safari on iPhone  when the user want to enter her cc details
                        'font-size': '18px',
                        'font-family': 'Arimo, sans-serif', // FIXME: use styles and global css-variables
                        'font-weight': '700',
                        'padding': '6px 12px'
                    },
                    'input::placeholder': {
                        'direction': ltr ? 'ltr' : 'rtl',
                    },
                    '.credit_card_number': {
                        'direction': 'ltr',
                    },
                    '.cvv': {
                        'padding-left': '50px',
                    }
                },
            });
            fields.onEvent('validityChange', dispatchTzlaFieldsState);
            fields.onEvent('blur', dispatchTzlaFieldsState);
            fields.onEvent('focus', dispatchTzlaFieldsState);
            fields.onEvent('notEmpty', dispatchTzlaFieldsState);
            fields.onEvent('empty', dispatchTzlaFieldsState);
            fields.onEvent('cardTypeChange', e => setCurrentService(e.cardType));
            setTzlaFields(fields);
        }
    }, [tzlaHostedFieldsApiReady, cardNumPlaceholder, cvvPlaceholder, orderStatusChecked, paymentPlan]);


    function tzlaFieldsReducer(state, action) {
        switch (action.type) {
            case 'blur': state[action.field].touched = true; break;
            case 'focus': state[action.field].touched = false; break;
            case 'empty': state[action.field].empty = true; break;
            case 'notEmpty': state[action.field].empty = false; break;
            case 'validityChange': state[action.field].valid = action.isValid; break;
            default:
                // do nothing
                break;
        }
        state.valid = Object.entries(state)
            .filter(([k, v]) => k !== 'valid')
            .map(([k, v]) => v)
            .map(v => !v.empty && v.valid).reduce((acc, v) => acc && v, true);
        return Object.assign({}, state);
    }

    function handleTzlaHostedFieldsApiLoadError(event) {
        NotificationManager.warning(t('payments.tzla_script_load_failed'), '', 5000);
        console.error(event);
        Sentry.captureMessage('Tranzila hosted fields script loading failed', 'error');
    }

    async function handleFormSubmit(values, helpers) {
        if (tzlaFieldsState.valid || dryRun) {
            const isLoaned = paymentPlan.paymentGroups.some(g => g.loaned);
            if (isLoaned) {
                await chargeByGroups(values.expireMonth, values.expireYear)
            } else {
                await chargeAtOnce(values.expireMonth, values.expireYear);
            }
        }
    }

    function toggleDryRun() {
        NotificationManager.info(dryRun ? 'Disabled' : 'Enabled', 'Dry run mode', 2000);
        setDryRun(!dryRun);
    }

    function charge(payload) {
        return new Promise((resolve, reject) => {
            tzlaFields.charge(payload, (err, response) => {
                if (err) {
                    reject({ err, response });
                } else {
                    resolve(response);
                }
            });
        });
    }

    async function chargeByGroups(expireMonth, expireYear) {
        const payload = {
            terminal_name: paymentPlan.paymentTerminal,
            cartId: paymentPlan.cartID,
            expiry_month: expireMonth,
            expiry_year: `20${expireYear}`,
            tran_mode: 'A',
            currency_code: paymentPlan.currency,
            tokenize: true,
        };

        const groups = paymentPlan.paymentGroups.filter(g => g.loaned);
        const nonLoanedGroups = paymentPlan.paymentGroups.filter(g => !g.loaned);
        if (nonLoanedGroups.length > 0) {
            const nonLoanedGroup = nonLoanedGroups.reduce((acc, group) => {
                acc.Amount += group.Amount;
                acc.PaymentID.push(group.PaymentID);
                acc.PaymentPlanID.push(group.PaymentPlanID);
                return acc;
            }, { Amount: 0, PaymentID: [], PaymentPlanID: [], loaned: false });
            groups.push(nonLoanedGroup);
        }
        const errors = [];

        dispatch(showLoader());

        for (const paymentGroup of groups) {
            try {
                const amount = paymentGroup.Amount;
                const groupPayload = {
                    ...payload,
                    amount,
                    loan: 'true', // this enables partial charge logic in callback handler
                    paymentId: Array.isArray(paymentGroup.PaymentID) ? paymentGroup.PaymentID.join(',') : paymentGroup.PaymentID,
                    PaymentPlanId: Array.isArray(paymentGroup.PaymentPlanID) ? paymentGroup.PaymentPlanID.join(',') : paymentGroup.PaymentPlanID,
                };
                if (paymentGroup.loaned) {
                    // TODO: checkup this calculation and probably move to the server side
                    const installmentAmount = Money.roundNumber(amount/paymentGroup.installments, 2);
                    groupPayload.payment_plan = 8;
                    groupPayload.first_installment_amount = amount - (installmentAmount * (paymentGroup.installments - 1));
                    groupPayload.other_installments_amount = installmentAmount;
                    groupPayload.total_installments_number = paymentGroup.installments;
                }
                console.log('groupPayload', groupPayload);
                const resp = dryRun ? { transaction_response: { success: true } } : await charge(groupPayload);
                const hasErrors = resp.errors?.length > 0 ?? false;
                const errorResponse = !(resp.transaction_response?.success ?? false);
                if (hasErrors) {
                    errors.push({ paymentGroup, resp });
                    console.error('Payment charge errors! response from tranzila', resp);
                    NotificationManager.error(t('card_page.payment_error'), '', 10000);
                    GAEvent(constants.GoogleAnalytics.Events.creditCard, 'ErrorsInCharge', amount, 'ErrorsInCharge');
                    break;
                } else if (errorResponse){
                    errors.push({ paymentGroup, resp });
                    console.error('Error in charging Text', resp);
                    const errorText = resp.transaction_response.error;
                    NotificationManager.error(errorText, '', 10000);
                    GAEvent(constants.GoogleAnalytics.Events.creditCard, 'ErrorsInChargeText', amount, errorText);
                    break;
                } else {
                    GAEvent(constants.GoogleAnalytics.Events.creditCard, 'SuccessCharge', amount, constants.GoogleAnalytics.Labels.creditCard.bllinkUI);
                    GAEvent(constants.GoogleAnalytics.Events.creditCard, 'BuildingSuccessCharge', 1, buildingID);
                }
            } catch (e) {
                console.error('error in charging callback', e.err);
                GAEvent('creditCardErrors', 'ChargeError', 1, e.err);
                setTzlaError(e.err);
                errors.push({ paymentGroup, e });
                break;
            }
        }
        const allPaymentIds = paymentPlan.paymentGroups.map(g => g.PaymentID);
        const failedPaymentIds = errors.map(e => e.paymentGroup.PaymentID);
        const notFailedPaymentIds = allPaymentIds.filter(id => !failedPaymentIds.some(failedId => failedId === id));

        if (notFailedPaymentIds.length === 0) {
            // TODO: add a new message text here
            NotificationManager.error(t('card_page.payment_error'), '', 10000);
            dispatch(hideLoader());
            return;
        }

        const statusesToWaitFor = constants.PaymentPlanDetailsStatus.ALL_SUCCESS;
        const result = await waitForPaymentState(new ServerData(), notFailedPaymentIds, statusesToWaitFor);

        dispatch(hideLoader());

        if (result.success) {
            GAEvent(constants.GoogleAnalytics.Events.creditCard, `redirectToStatusAfterSuccess_try_${result.counter}`);
            NotificationManager.success(t('payments.payment_confirmation'));
            triggerRewardsNotification();
            if (disabledInsurancePage){
                dispatch(resetTenant());
                history.push(`/status/${cartID}`);
            } else{
                // https://bllink.sentry.io/issues/4244968139/?project=5424386&query=is%3Aunresolved&referrer=issue-stream&statsPeriod=1h&stream_index=0
                history.push({pathname: `/${constants.pages.insuranceOffer}/${buildingID}/${tenantDetails?.apartmentNumber || '1'}/${cartID}`});
            }
        } else {
            switch (result.reason) {
                case 'ERROR':
                    // TODO: add a new message text here
                    NotificationManager.error(result.error.message, '', 10000);
                    break;
                case "TIMEOUT":
                    // TODO: add a new message text here
                    console.error('Payment operation timeout', result);
                    GAEvent(constants.GoogleAnalytics.Events.creditCard, 'didntRedirectStill', 1, result.timeLeft);
                    break;
                default:
                    // NOTE: should never happen
                    throw new Error(`Unknown reason ${result.reason}`);
            }
        }
    }

    async function chargeAtOnce(expireMonth, expireYear) {
        const isFuturePayment = paymentPlan.isFuturePayment;
        // use "amount: 1" to avoid catching the user's credit limit
        // the point is to get the token for a charge at a later date and not to charge now
        // part use case is users who DON'T have the credit limit and will have in a later date
        const amount = isFuturePayment ? 1 : (paymentPlan.firstPaymentTotal - (paymentPlan.firstRewardTotal ?? 0));
        const payload = {
            // REQUIRED PARAMS
            terminal_name: paymentPlan.paymentTerminal,
            amount,
            // OPTIONAL FIELDS
            expiry_month: expireMonth,
            expiry_year: `20${expireYear}`,
            tokenize: true, // to get the token back in the callback
            // feel free to clean up once you know proper field name
            tran_mode: isFuturePayment ? 'V' : undefined,
            tranmode: isFuturePayment ? 'V' : undefined,
            transaction_mode: isFuturePayment ? 'V' : undefined,
            currency_code: paymentPlan.currency,
            // User Fields
            cartId: paymentPlan.cartID,
            PaymentPlanId: '',
            paymentId: '',
            loan: 'false',
        }
        console.log('Tranzila charge payload', payload);
        dispatch(showLoader());
        setTzlaError(null);
        tzlaFields.charge(payload, (err, response) => {
            handleTranzilaChargeResponse(err, response, amount);
        });
    }

    function handleTranzilaChargeResponse(err, response, amount) {
        if (err) { // object or false
            console.error('error in charging callback', err);
            setTzlaError(err);
            GAEvent('creditCardErrors', 'ChargeError', 1, err);
            dispatch(hideLoader()); // started before tzlaFields.charge call
            return;
        }
        const hasErrors = response.errors?.length > 0;
        const errorResponse = !(response.transaction_response?.success ?? false);
        if (hasErrors) {
            console.error('Payment charge errors! response from tranzila', response);
            NotificationManager.error(t('card_page.payment_error'), '', 10000);
            GAEvent(constants.GoogleAnalytics.Events.creditCard, 'ErrorsInCharge', amount, 'ErrorsInCharge');
            dispatch(hideLoader()); // started before tzlaFields.charge call
        } else if (errorResponse){
            console.error('Error in charging Text', response);
            const errorText = response.transaction_response.error;
            NotificationManager.error(errorText, '', 10000);
            GAEvent(constants.GoogleAnalytics.Events.creditCard, 'ErrorsInChargeText', amount, errorText);
            dispatch(hideLoader()); // started before tzlaFields.charge call
        } else {
            setTzlaError(null);
            GAEvent(constants.GoogleAnalytics.Events.creditCard, 'SuccessCharge', amount, constants.GoogleAnalytics.Labels.creditCard.bllinkUI);
            GAEvent(constants.GoogleAnalytics.Events.creditCard, 'BuildingSuccessCharge', 1, buildingID);
            waitForOrderStatus(new ServerData(), 1);
        }
    }

    async function waitForOrderStatus(client, counter) {
        try {
            const order = await client.tenants.payments.getOrderStatus(cartID);
            const success = constants.PaymentPlanDetailsStatus.ALL_SUCCESS.includes(order.payment_details.state);
            if (success) {
                dispatch(hideLoader()); // started before tzlaFields.charge call
                GAEvent(constants.GoogleAnalytics.Events.creditCard, `redirectToStatusAfterSuccess_try_${counter}`);
                NotificationManager.success(t('payments.payment_confirmation'));
                triggerRewardsNotification();
                if (disabledInsurancePage){
                    dispatch(resetTenant());
                    history.push(`/status/${cartID}`);
                } else{
                    // https://bllink.sentry.io/issues/4244968139/?project=5424386&query=is%3Aunresolved&referrer=issue-stream&statsPeriod=1h&stream_index=0
                    history.push({pathname: `/${constants.pages.insuranceOffer}/${buildingID}/${tenantDetails?.apartmentNumber||'1'}/${cartID}`});
                }
            } else {
                // TODO: if more than 10 seconds has passed, show an error message
                window.setTimeout(() => waitForOrderStatus(client, counter + 1), POLLING_PERIOD);
                if (counter * POLLING_PERIOD > MAX_WAIT_TIME){
                    GAEvent(constants.GoogleAnalytics.Events.creditCard, 'didntRedirectStill', 1, counter * POLLING_PERIOD);
                }
            }
        } catch (e) {
            // may be caused by client.tenants.payments.getOrderStatus call, this case error will be shown by client
            // we only need here stop loading wheel
            dispatch(hideLoader()); // started before tzlaFields.charge call
        }
    }

    /**
     * @param {ServerData} client
     * @param {number[]} paymentIds
     * @param {number[]} states
     * @return {Promise<{ success: boolean, reason?: 'ERROR' | 'TIMEOUT', counter: number, error?: Error, timeLeft?: number }>}
     * */
    async function waitForPaymentState(client, paymentIds, states) {
        const isInState = ([paymentId, state]) => states.some(s => s === state);
        const sleep = (t) => new Promise((res, rej) => window.setTimeout(res, t));

        let success = false;
        let counter = 0;
        while (!success) {
            counter++;
            try {
                const paymentStates = await client.tenants.payments.getPaymentStates(paymentIds);
                success = paymentStates.every(isInState);
            } catch (error) {
                return { success: false, counter, reason: 'ERROR', error };
            }
            if (!success) {
                const timeLeft = counter * POLLING_PERIOD;
                if (timeLeft > MAX_WAIT_TIME) {
                    return { success: false, counter, reason: 'TIMEOUT', timeLeft };
                } else {
                    await sleep(POLLING_PERIOD);
                }
            }
        }
        return { success: true, counter };
    }

    function CustomFieldError({ filedName }) {
        const state = tzlaFieldsState[filedName];
        console.log(tzlaFieldsState);
        if (state.touched) {
            if (state.empty) {
                return <div className="error">{t('titles.forms.mandatory_field')}</div>;
            }
            if (!state.valid) {
                if (filedName === 'credit_card_number') return <div className="error">{t('titles.forms.invalid_card_number')}</div>;
                if (filedName === 'cvv') return <div className="error">{t('titles.forms.invalid_cvv_code')}</div>;
            }
        }
        return '';
    }

    function PaymentErrors() {
        // payment_provider_error
        console.log('PaymentErrors', tzlaError);
        if (tzlaError == null) return '';
        if (tzlaError.messages != null) {
            return tzlaError.messages.map(errMsg => (<p className="error">{errMsg.message}</p>))
        }
        return <p className="error">{t('card_page.payment_provider_error')}</p> ;
    }

    const onDateChange = (e, type) => setDate({
        ...date,
        [type]: e.target.value
    });

    return (
        <PaymentFlowContainer title={t('payments.payment_for')} subheader={t('payments.checkout_info')}>


            <StyledCreditCardPage ltr={ltr}>
                <div className='credit-card'>
                    <div className='top-row'>
                        <img className='logo' src={CARDS[currentService]?.image ?? CARDS['unknown'].image} />
                        <img className='contactless' src={contactless} />
                    </div>

                    <div className='middle-row'>
                        <div>Israel Israeli</div>
                        <img className='chip' src={chip}/>
                    </div>

                    <div className='bottom-row'>
                        <div className='card-digits'>
                            <span>* * * *</span>
                            <span>* * * *</span>
                            {/* NOTE: we have no access to card number */}
                        </div>
                        <div>
                            <span>{date.month}</span>/<span>{date.year}</span>
                        </div>
                    </div>
                </div>

                <div className='credit-card-block-wrapper'>
                    <div className='credit-card-and-actions-block credit-card-page-block'>
                        {/* <div className="payment-amount">
                            <span>{t('card_page.total_payable')}</span>
                            <CurrencyValue currency={paymentPlan?.currency} notInCents={true}>{paymentPlan?.firstPaymentTotal - (paymentPlan?.firstRewardTotal ?? 0)}</CurrencyValue>
                        </div> */}
                        <PaymentErrors />
                        <Formik
                            initialValues={{ expireMonth: '', expireYear: '' }}
                            validationSchema={formSchema}
                            enableReinitialize
                            onSubmit={handleFormSubmit}
                        >{({
                            values,
                            handleChange,
                            handleBlur,
                            handleSubmit,
                        }) => {
                            return (
                                <form onSubmit={handleSubmit}>

                                    <div>
                                        <label htmlFor="credit-card-number">{t('card_page.card_number')}</label>
                                        <div id="credit-card-number" ref={cardNumPlaceholder} className="bllink-input-border" />
                                        <CustomFieldError filedName="credit_card_number" />
                                    </div>

                                    <div className='details-wrapper'>
                                        <div className='date'>
                                            <div>
                                                <Input
                                                    name='expireMonth'
                                                    label={t('card_page.expiry')}
                                                    value={values.month}
                                                    onChange={e => {handleChange(e); onDateChange(e, 'month')}}
                                                    onBlur={handleBlur}
                                                    tabIndex={2}
                                                    maxLength={2}
                                                    inputMode="numeric"
                                                />
                                                <ErrorMessage name="expireMonth">{msg => <div className="error">{msg}</div>}</ErrorMessage>
                                            </div>
                                            <span className='separator'>/</span>
                                            <div>
                                                <Input
                                                    name='expireYear'
                                                    value={values.year}
                                                    onChange={e => {handleChange(e); onDateChange(e, 'year')}}
                                                    onBlur={handleBlur}
                                                    tabIndex={3}
                                                    maxLength={2}
                                                    inputMode="numeric"
                                                />
                                                <ErrorMessage name="expireYear">{msg => <div className="error">{msg}</div>}</ErrorMessage>
                                            </div>
                                        </div>

                                        <div className='cvv-wrapper'>
                                            <div className='dots'>
                                                <div className='dot'></div>
                                                <div className='dot'></div>
                                                <div className='dot'></div>
                                                <div className='dot'></div>
                                                <div className='dot'></div>
                                                <div className='dot'></div>
                                                <div className='dot'></div>
                                                <div className='dot'></div>
                                                <div className='dot'></div>
                                            </div>
                                            <label htmlFor="credit-card-cvv">{t('card_page.cvv')}</label>
                                            <div id="credit-card-cvv" ref={cvvPlaceholder} className="bllink-input-border"/>
                                            <CustomFieldError filedName="cvv" />
                                        </div>
                                    </div>

                                    <div className='btn-wrapper'>
                                        <Button type="submit"><span>{t('expenses.set_expenses.continue')}</span><span>{dryRun ? '[Dry Run]' : ''}</span> <img className='arrow' src={arrowInCircle} /></Button>
                                    </div>
                                </form>
                            )}}
                        </Formik>
                    </div>

                    <div className='payment-services'>
                        <div className='header'>{t('payments.payment_services')}</div>
                        <div className='images'>
                            <div className='img-wrapper'><img alt='visa' src={VisaIcon}/></div>
                            <div className='img-wrapper'><img alt='master_card' src={MasterCardIcon}/></div>
                            <div className='img-wrapper'><img alt='diners' src={DinersIcon}/></div>
                            <div className='img-wrapper'><img alt='ae' src={AmericanExpressIcon}/></div>
                        </div>
                    </div>

                </div>
            </StyledCreditCardPage>
        </PaymentFlowContainer>
    )
}
